import {
  type PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { ArdoqId } from '@ardoq/api-types';
import { formatDateTimeShort } from '@ardoq/date-time';
import {
  type FeatureContext,
  featureOperations,
  Features,
} from '@ardoq/features';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import { toAttachmentSizeString } from '../toDocumentArchivePageViewProps';
import type { DocumentArchiveDuplicatesProps } from '../DocumentArchivePageView';
import type { DuplicatesState } from './duplicates$';
import type { DuplicateCopy, DuplicateGroup } from './toDuplicateGroups';
import type {
  DuplicateCopyProps,
  DuplicateGroupProps,
  DuplicatesModalFooterProps,
  DuplicatesModalProps,
} from './DuplicatesModal';

export type DuplicatesCommands = {
  findDuplicates: VoidFunction;
  toggleSelected: (id: ArdoqId) => void;
  close: VoidFunction;
  requestDeletion: (attachmentIds: ArdoqId[]) => void;
};

export type FindDuplicatesButtonProps = {
  label: string;
  isLoading: boolean;
  loadingHint: string | null;
  onClick: VoidFunction;
};

const isRemovable = (attachment: DuplicateCopy): boolean =>
  attachment.status === 'remove';

const hasRemovableCopies = (group: DuplicateGroup): boolean =>
  group.attachments.some(isRemovable);

type ToGroupPropsArgs = {
  selectedIds: ArdoqId[];
  locale: Locale;
  commands: DuplicatesCommands;
  i18nLocale: I18nLocale;
};

const toCopyProps = (
  attachment: DuplicateCopy,
  { selectedIds, locale, commands, i18nLocale }: ToGroupPropsArgs
): DuplicateCopyProps => ({
  id: attachment._id,
  label: t(
    i18nLocale,
    'documentArchive:duplicates.unusedCopy',
    { created: formatDateTimeShort(attachment.created, locale) },
    'Unused copy, uploaded {{created}}'
  ),
  isChecked: selectedIds.includes(attachment._id),
  onToggle: () => commands.toggleSelected(attachment._id),
});

const toKeptText = (keptCount: number, i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'documentArchive:duplicates.kept',
    { count: keptCount },
    keptCount === 1 ? 'Keeping 1 copy' : 'Keeping {{count}} copies'
  );

const toGroupProps = (
  group: DuplicateGroup,
  args: ToGroupPropsArgs
): DuplicateGroupProps => {
  const removable = group.attachments.filter(isRemovable);
  return {
    id: group.attachments.map(attachment => attachment._id).join(),
    title: group.filename,
    subtitle: toAttachmentSizeString(group.size),
    keptText: toKeptText(
      group.attachments.length - removable.length,
      args.i18nLocale
    ),
    copies: removable.map(attachment => toCopyProps(attachment, args)),
  };
};

const toSummaryText = (
  removableCount: number,
  i18nLocale: I18nLocale
): string => {
  if (removableCount === 0) {
    return t(
      i18nLocale,
      'documentArchive:duplicates.nothingToDelete',
      'Some files have identical copies, but every copy is in use. There is nothing to delete.'
    );
  }
  return t(
    i18nLocale,
    'documentArchive:duplicates.summary',
    { count: removableCount },
    removableCount === 1
      ? '1 unused copy can be deleted.'
      : '{{count}} unused copies can be deleted.'
  );
};

const toHiddenText = (
  hiddenCount: number,
  removableCount: number,
  i18nLocale: I18nLocale
): string | null => {
  if (hiddenCount === 0 || removableCount === 0) {
    return null;
  }
  return t(
    i18nLocale,
    'documentArchive:duplicates.hidden',
    { count: hiddenCount },
    hiddenCount === 1
      ? '1 more file has copies that are all in use.'
      : '{{count}} more files have copies that are all in use.'
  );
};

const toFooterProps = (
  removableCount: number,
  selectedIds: ArdoqId[],
  commands: DuplicatesCommands,
  i18nLocale: I18nLocale
): DuplicatesModalFooterProps => {
  if (removableCount === 0) {
    return {
      kind: 'close',
      closeButtonText: t(
        i18nLocale,
        'documentArchive:duplicates.close',
        'Close'
      ),
      onClose: commands.close,
    };
  }
  return {
    kind: 'delete',
    cancelButtonText: t(
      i18nLocale,
      'documentArchive:duplicates.cancel',
      'Cancel'
    ),
    deleteButtonText: t(
      i18nLocale,
      'documentArchive:duplicates.delete',
      { count: selectedIds.length },
      'Delete selected ({{count}})'
    ),
    isDeleteDisabled: selectedIds.length === 0,
    onDelete: () => commands.requestDeletion(selectedIds),
    onCancel: commands.close,
  };
};

const toDuplicatesModalProps = (
  state: DuplicatesState,
  locale: Locale,
  commands: DuplicatesCommands,
  i18nLocale: I18nLocale
): DuplicatesModalProps => {
  const groups = state.status === 'loaded' ? state.groups : [];
  const selectedIds = state.status === 'loaded' ? state.selectedIds : [];
  const deletableGroups = groups.filter(hasRemovableCopies);
  const removableCount = deletableGroups.flatMap(group =>
    group.attachments.filter(isRemovable)
  ).length;

  return {
    isOpen: state.status === 'loaded',
    headerText: t(
      i18nLocale,
      'documentArchive:duplicates.header',
      'Duplicate files'
    ),
    summaryText: toSummaryText(removableCount, i18nLocale),
    hiddenText: toHiddenText(
      groups.length - deletableGroups.length,
      removableCount,
      i18nLocale
    ),
    groups: deletableGroups.map(group =>
      toGroupProps(group, { selectedIds, locale, commands, i18nLocale })
    ),
    footer: toFooterProps(removableCount, selectedIds, commands, i18nLocale),
    onCancel: commands.close,
  };
};

const toFindDuplicatesButtonProps = (
  state: DuplicatesState,
  canFindDuplicates: boolean,
  commands: DuplicatesCommands,
  i18nLocale: I18nLocale
): FindDuplicatesButtonProps | null => {
  if (!canFindDuplicates) {
    return null;
  }
  const isLoading = state.status === 'loading';
  return {
    label: t(
      i18nLocale,
      'documentArchive:duplicates.findButton',
      'Find duplicates'
    ),
    isLoading,
    loadingHint: isLoading
      ? t(
          i18nLocale,
          'documentArchive:duplicates.loadingHint',
          'Please keep this window open. Processing large batches may take a few minutes. Closing this tab will cancel your session.'
        )
      : null,
    onClick: commands.findDuplicates,
  };
};

const canFindDuplicates = (
  permissionContext: PermissionContext,
  featureContext: FeatureContext
): boolean =>
  permissionsOperations.hasAdminRole(permissionContext) &&
  featureOperations.hasFeature(
    featureContext,
    Features.CORE_TMP_ATTACHMENT_DEDUPLICATION
  );

export const toDocumentArchiveDuplicatesProps = (
  state: DuplicatesState,
  permissionContext: PermissionContext,
  featureContext: FeatureContext,
  locale: Locale,
  commands: DuplicatesCommands,
  i18nLocale: I18nLocale
): DocumentArchiveDuplicatesProps => ({
  findButton: toFindDuplicatesButtonProps(
    state,
    canFindDuplicates(permissionContext, featureContext),
    commands,
    i18nLocale
  ),
  modal: toDuplicatesModalProps(state, locale, commands, i18nLocale),
});
